/**
 * Módulo de Marcas
 * 
 * Renderiza um grid responsivo de logos de marcas com design system
 * Características:
 * - Layout responsivo (4 → 5 → 7 → 9 → 11 colunas)
 * - Efeitos de hover otimizados
 * - Animação sequencial de entrada
 * - Acessibilidade WCAG 2.1
 * - Lazy loading de imagens
 * 
 * @module MarcasModule
 */

import BaseModule from './BaseModule.js';

// ────────────────────────────────────────────────────────────
// CONSTANTES
// ────────────────────────────────────────────────────────────

const DEFAULTS = {
    container: null,
    injectStyles: true,
    maxWidth: '1200px',
    animateIn: true,
    animationDelay: 30  // ms entre items
};

const CSS_CLASSES = {
    wrapper: 'mzk-marcas-wrapper',
    grid: 'mzk-marcas-grid',
    item: 'mzk-marcas-item',
    logo: 'mzk-marcas-logo',
  name: 'mzk-marcas-name',
    empty: 'mzk-marcas-empty'
};

const ANIMATION = {
    name: 'mzk-marcas-fadeIn',
    duration: '0.5s',
    timing: 'ease'
};

const PATHS = {
    styles: '/src/modules/MarcasModule.css'
};

// ────────────────────────────────────────────────────────────
// MÓDULO
// ────────────────────────────────────────────────────────────

export default class MarcasModule extends BaseModule {
    static defaultConfig = DEFAULTS;

    constructor(config = {}) {
        super({ ...MarcasModule.defaultConfig, ...config });
        this.data = null;
        this.styleInjected = false;
    }

    /**
     * Inicializa o módulo
     * @throws {Error} Se data não for fornecida
     */
    async init() {
        this.validateInput();
        this.data = this.config.data;

        if (this.config.injectStyles && !this.styleInjected) {
            await this.loadStyles();
            this.styleInjected = true;
        }

        this.initialized = true;
    }

    /**
     * Renderiza as marcas
     * @param {HTMLElement} container - Container para renderização
     * @throws {Error} Se módulo não foi inicializado ou container inválido
     */
    render(container) {
        if (!this.initialized) {
            throw new Error('Módulo não foi inicializado. Chame init() primeiro.');
        }

        this.container = container || this.config.container;
        if (!this.container) {
            throw new Error('Container é obrigatório');
        }

        const html = this.buildHTML();
        this.container.innerHTML = html;

        if (this.config.animateIn) {
            this.animateItems();
        }
    }

    /**
     * Atualiza conteúdo das marcas
     * @param {Object} newData - Novos dados
     */
    update(newData) {
        this.data = newData;
        this.render();
    }

    /**
     * Destrói o módulo e libera recursos
     */
    destroy() {
        super.destroy();
        this.data = null;
    }

    // ────────────────────────────────────────────────────────────
    // PRIVATE: VALIDAÇÃO
    // ────────────────────────────────────────────────────────────

    /**
     * Valida entrada do módulo
     * @private
     * @throws {Error}
     */
    validateInput() {
        if (!this.config.data) {
            throw new Error('MarcasModule requer "data" na configuração');
        }
    }

    // ────────────────────────────────────────────────────────────
    // PRIVATE: ESTILOS
    // ────────────────────────────────────────────────────────────

    /**
     * Carrega CSS do módulo (inline)
     * @private
     * @returns {Promise<void>}
     */
    async loadStyles() {
        const styleId = 'mzk-marcas-stylesheet';

        if (document.getElementById(styleId)) {
            return; // Já foi carregado
        }

        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = MarcasModule.getStyles();
        document.head.appendChild(style);
    }

    // ────────────────────────────────────────────────────────────
    // PRIVATE: RENDERIZAÇÃO
    // ────────────────────────────────────────────────────────────

    /**
     * Constrói HTML completo
     * @private
     * @returns {string} HTML markup
     */
    buildHTML() {
        const { marcas } = this.data;

        if (!this.isValidMarcasList(marcas)) {
            return this.buildEmptyState();
        }

        const grid = this.buildGrid(marcas);

        return `
            <div class="${CSS_CLASSES.wrapper}">
                ${grid}
            </div>
        `.trim();
    }

    /**
     * Constrói grid de marcas
     * @private
     * @param {Array} marcas
     * @returns {string}
     */
    buildGrid(marcas) {
        const items = marcas
            .filter(marca => marca.logo)
            .map(marca => this.buildItem(marca))
            .join('');

        return `
            <ul class="${CSS_CLASSES.grid}" role="list">
                ${items}
            </ul>
        `.trim();
    }

    /**
     * Constrói item individual de marca
     * @private
     * @param {Object} marca
     * @returns {string}
     */
    buildItem(marca) {
        const { nome, logo, descricao } = marca;
      const safeName = this.escapeHtml(nome || 'Marca');
        const title = descricao ? `title="${this.escapeHtml(descricao)}"` : '';

        return `
            <li class="${CSS_CLASSES.item}" ${title}>
                <div class="${CSS_CLASSES.logo}" role="presentation">
                    <img 
                        src="${this.escapeHtml(logo)}"
              alt="${safeName}"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
          <span class="${CSS_CLASSES.name}">${safeName}</span>
            </li>
        `.trim();
    }

    /**
     * Constrói estado vazio
     * @private
     * @returns {string}
     */
    buildEmptyState() {
        return `
            <div class="${CSS_CLASSES.wrapper}">
                <ul class="${CSS_CLASSES.grid}" role="list">
                    <li class="${CSS_CLASSES.empty}">Nenhuma marca disponível</li>
                </ul>
            </div>
        `.trim();
    }

    // ────────────────────────────────────────────────────────────
    // PRIVATE: ANIMAÇÃO
    // ────────────────────────────────────────────────────────────

    /**
     * Anima entrada dos items
     * @private
     */
    animateItems() {
        const items = this.container.querySelectorAll(`.${CSS_CLASSES.item}`);

        if (items.length === 0) return;

        items.forEach((item, index) => {
            item.style.opacity = '0';
            item.style.animation = `${ANIMATION.name} ${ANIMATION.duration} ${ANIMATION.timing} ${index * this.config.animationDelay}ms forwards`;
        });
    }

    // ────────────────────────────────────────────────────────────
    // PRIVATE: UTILIDADES
    // ────────────────────────────────────────────────────────────

    /**
     * Valida se marcas é array válido
     * @private
     * @param {*} marcas
     * @returns {boolean}
     */
    isValidMarcasList(marcas) {
        return Array.isArray(marcas) && marcas.length > 0;
    }

    /**
     * Escapa HTML para prevenir XSS
     * @private
     * @param {string} text
     * @returns {string}
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Retorna CSS do módulo (inline)
     * @static
     * @returns {string} CSS styles
     */
    static getStyles() {
        return `
:root {
  --color-neutral-50: #f5f5f5;
  --color-neutral-100: #e0e0e0;
  --color-neutral-300: #9e9e9e;
  --color-neutral-500: #616161;
  --color-neutral-700: #2e2e2e;
  --color-neutral-900: #1a1a1a;
  --color-primary-50: #e6f7f5;
  --color-primary-100: #b3e8e2;
  --color-primary-500: #00c4a7;
  --color-primary-700: #009e87;
  --color-primary-900: #006b5c;
  --color-surface: #2e2e2e;
  --color-surface-hover: #383838;
  --color-border: #3a3a3a;
  --shadow-card-hover: 0 6px 24px rgba(0, 196, 167, 0.18);
  --transition-medium: 250ms ease;
  --radius-full: 9999px;
}

.mzk-marcas-wrapper {
  width: 100%;
  margin: 0 auto;
  padding: 1rem 1rem;
}

@media (min-width: 768px) {
  .mzk-marcas-wrapper {
    padding: 1rem 1.5rem;
  }
}

.mzk-marcas-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.mzk-marcas-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  flex: 0 0 calc(50% - 0.5rem);
  max-width: 375px;
  min-height: 138px;
  transition: transform var(--transition-medium), box-shadow var(--transition-medium);
}

@media (min-width: 600px) {
  .mzk-marcas-item {
    flex: 0 0 calc(33.333% - 0.7rem);
    gap: 0.5rem;
    min-height: 182px;
  }
}

@media (min-width: 900px) {
  .mzk-marcas-item {
    flex: 0 0 calc(25% - 0.75rem);
  }
}

.mzk-marcas-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.mzk-marcas-name {
  margin: 0;
  text-align: center;
  color: var(--color-neutral-100);
  font-size: 12px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

@media (min-width: 600px) {
  .mzk-marcas-name {
    font-size: 18px;
  }
}

@media (min-width: 900px) {
  .mzk-marcas-name {
    font-size: 22px;
  }
}

.mzk-marcas-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-full);
  cursor: default;
}

@media (min-width: 600px) {
  .mzk-marcas-logo {
    width: 96px;
    height: 96px;
  }
}

@media (min-width: 900px) {
  .mzk-marcas-logo {
    width: 120px;
    height: 120px;
  }
}

.mzk-marcas-logo img {
  width: 80%;
  height: 80%;
  max-width: 100%;
  object-fit: contain;
}

.mzk-marcas-empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 2rem;
  color: var(--color-neutral-500);
  font-size: 0.95rem;
  margin: 0;
}

@keyframes mzk-marcas-fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .mzk-marcas-item,
  .mzk-marcas-logo,
  .mzk-marcas-logo img {
    transition: none;
  }

  .mzk-marcas-item:hover {
    transform: none;
    box-shadow: none;
  }
}
        `.trim();
    }
}
